/**
 * SubtaskProgressIndicator — compact progress indicator for card tiles in the board view.
 * Shows "N/M" text with a mini progress bar. Uses green for fully complete,
 * blue for in-progress, and gray for zero progress.
 *
 * Validates: Requirements 13.1, 13.2, 13.3, 13.4
 */

import React from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SubtaskProgressIndicatorProps {
  /** Number of completed subtasks */
  readonly completed: number;
  /** Total number of subtasks */
  readonly total: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SubtaskProgressIndicator({
  completed,
  total,
}: SubtaskProgressIndicatorProps): React.ReactElement | null {
  if (total <= 0) {
    return null;
  }

  const percentage = Math.round((completed / total) * 100);
  const barColorClass = getBarColorClass(completed, total);

  return (
    <div
      className="flex items-center gap-1.5"
      aria-label={`${completed} of ${total} subtasks complete`}
      role="group"
    >
      <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">
        {completed}/{total}
      </span>
      <div
        className="h-1.5 w-12 rounded-full bg-gray-200 overflow-hidden"
        role="progressbar"
        aria-valuenow={completed}
        aria-valuemin={0}
        aria-valuemax={total}
      >
        <div
          className={`h-full rounded-full transition-all ${barColorClass}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getBarColorClass(completed: number, total: number): string {
  if (completed === 0) {
    return 'bg-gray-300';
  }
  if (completed === total) {
    return 'bg-green-500';
  }
  return 'bg-blue-500';
}
